/**
 * lifecycleColumnManifest.js
 * ──────────────────────────
 * Authoritative column manifest for the Lifecycle Recon Grid View.
 * Extracted from LifecycleDetailPanel.jsx — every field displayed or editable
 * in the detail panel is represented here.
 *
 * container: 'tool' = read-only context sourced from the tool record
 *            'lifecycle' = editable lifecycle profile field
 *
 * type: 'text' | 'number' | 'date' | 'boolean' | 'combobox'
 * refKey: ReferenceData key for combobox fields (matches useReferenceData field names)
 * readOnly: true = never editable in the grid (Phase 2 editing skips these)
 */

export const LIFECYCLE_COLUMNS = [
  /* ── Read-only context columns (from tool record) ─────────────────── */
  { key: 'supplier_tool_id',  label: 'Tool Order',   type: 'text', readOnly: true, container: 'tool' },
  { key: 'alt_tool_id',       label: 'Alt Tool ID',  type: 'text', readOnly: true, container: 'tool' },
  { key: 'platform',          label: 'Platform',     type: 'text', readOnly: true, container: 'tool' },
  { key: 'tool_type',         label: 'Tool Type',    type: 'text', readOnly: true, container: 'tool' },
  { key: 'tool_description',  label: 'Description',  type: 'text', readOnly: true, container: 'tool' },
  { key: 'tier_level',        label: 'Tier',         type: 'text', readOnly: true, container: 'tool' },

  /* ── Editable lifecycle columns ────────────────────────────────────── */
  // Piece Counts section
  { key: 'actual_shots_to_date',  label: 'Actual Pieces',    type: 'number', readOnly: false, container: 'lifecycle' },
  { key: 'warranty_shots',        label: 'Warranty Pieces',  type: 'number', readOnly: false, container: 'lifecycle' },
  { key: 'weekly_capacity',       label: 'Weekly Capacity',  type: 'number', readOnly: false, container: 'lifecycle' },
  { key: 'warranty_shots_source', label: 'Warranty Source',  type: 'combobox', readOnly: false, container: 'lifecycle', refKey: 'warranty_shots_source' },

  // EOW Status section (all read-only — computed by backend)
  { key: 'eow_estimated_date',        label: 'EOW Estimated',       type: 'date',   readOnly: true, container: 'lifecycle' },
  { key: 'eow_decision_trigger_date', label: 'EOW Decision Trigger', type: 'date',  readOnly: true, container: 'lifecycle' },
  { key: 'disposition_category',       label: 'Disposition',         type: 'text',   readOnly: true, container: 'lifecycle' },

  // Condition section
  { key: 'current_condition_score', label: 'Condition Score', type: 'number', readOnly: false, container: 'lifecycle' },
  { key: 'assessment_notes',        label: 'Assessment Notes', type: 'text',  readOnly: false, container: 'lifecycle' },

  // SCR Tracking section
  { key: 'scr_status',        label: 'SCR Status',        type: 'combobox', readOnly: false, container: 'lifecycle', refKey: 'scr_status' },
  { key: 'scr_type',          label: 'SCR Type',          type: 'combobox', readOnly: false, container: 'lifecycle', refKey: 'scr_type' },
  { key: 'scr_submitted_date', label: 'SCR Submitted',    type: 'date',    readOnly: false, container: 'lifecycle' },
  { key: 'agreed_solution',   label: 'Agreed Solution',   type: 'text',    readOnly: false, container: 'lifecycle' },
  { key: 'scr_identifier',    label: 'SCR Identifier',    type: 'text',    readOnly: false, container: 'lifecycle' },
  { key: 'scr_notes',         label: 'SCR Notes',         type: 'text',    readOnly: false, container: 'lifecycle' },

  // Autoliv Import Fields section
  { key: 'ownership',      label: 'Ownership',      type: 'combobox', readOnly: false, container: 'lifecycle', refKey: 'ownership' },
  { key: 'alv_plant',      label: 'ALV Plant',      type: 'combobox', readOnly: true,  container: 'lifecycle', refKey: 'alv_plant' }, // read-only — scheduled for removal
  { key: 'service_type',   label: 'Service Type',   type: 'combobox', readOnly: false, container: 'lifecycle', refKey: 'service_type' },
  { key: 'shift_pattern',  label: 'Shift Pattern',  type: 'text',    readOnly: false, container: 'lifecycle' },
  { key: 'flag_yellow',    label: 'Flag Yellow',    type: 'boolean',  readOnly: false, container: 'lifecycle' },
  { key: 'flag_orange',    label: 'Flag Orange',    type: 'boolean',  readOnly: false, container: 'lifecycle' },
  { key: 'lifecycle_detail', label: 'Location Detail', type: 'combobox', readOnly: false, container: 'lifecycle', refKey: 'lifecycle_detail' },
  { key: 'cavity_number_start', label: 'Cavity Start', type: 'number', readOnly: false, container: 'lifecycle' },
  { key: 'cavity_number_end',   label: 'Cavity End',   type: 'number', readOnly: false, container: 'lifecycle' },
  { key: 'disposal_request_submitted_date', label: 'Disposal Request Date', type: 'date', readOnly: false, container: 'lifecycle' },

  // Financial / Asset Info section
  { key: 'original_asset_value',    label: 'Asset Value',    type: 'number', readOnly: true, container: 'lifecycle' },
  { key: 'original_asset_currency', label: 'Asset Currency', type: 'text',   readOnly: true, container: 'lifecycle' },
  { key: 'cost_type',               label: 'Cost Type',      type: 'text',   readOnly: true, container: 'lifecycle' },
  { key: 'finished_goods_on_hand',  label: 'FG on Hand',     type: 'number', readOnly: false, container: 'lifecycle' },
  { key: 'work_in_process',         label: 'WIP',            type: 'number', readOnly: false, container: 'lifecycle' },
  { key: 'raw_materials_on_hand',   label: 'Raw Materials',  type: 'number', readOnly: false, container: 'lifecycle' },

  // Header fields
  { key: 'list_number',   label: 'List',         type: 'text',    readOnly: false, container: 'lifecycle' },
  { key: 'recon_status',  label: 'Recon Status', type: 'combobox', readOnly: false, container: 'lifecycle', refKey: 'recon_status' },
];
